import 'isomorphic-unfetch';
import * as React from 'react';
import {Formik} from 'formik';
import ReCAPTCHA from "react-google-recaptcha";
import { Flex, Box } from 'reflexbox'
import { required, email } from '../../utils/validations';
import {
  Label,
  Button,
  Input,
  Textarea,
  Error,
  Success,
  Loader as LoaderStyle,
  Form,
} from '../../components/forms/styled/form'
import Loader from '../../components/Icons/Loader'
import { useRef } from 'react';
import Checkbox from "../../components/forms/Checkbox";
import { Translate } from '../../components/tr';

const variantNames = {
  ['original']: 'Original painting',
  ['fine art print']: 'Reproduction on fine art paper',
  ['canvas print']: 'Reproduction on the canvas',
}

interface FormProps {
  recaptchaKey: string;
  imgTitle: string;
  variants: {
    name: string,
    price: number,
  }[]
}

const BuyForm = ({
  recaptchaKey,
  variants,
  imgTitle,
}: FormProps) => {
  const recaptchaRef = useRef(null);

  const onSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const response = await fetch('/send-email', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...values,
          type: 'order',
          paintingTitle: imgTitle
        }),
      });

      if (response.ok) {
        setStatus({ msg: 'Email has been successfully sent, thank you!' });
      } else {
        setStatus({ msg: await response.text() });
      }
    } catch (e) {
      // @ts-expect-error
      setStatus({ msg: e.message });
    }

    setSubmitting(false);
  };

  const validate = (values) => {
    const errors = {
      email: required(values.email) || email(values.email),
      nickname: required(values.nickname),
      message: required(values.message),
      paintingType: required(values.paintingType),
      recaptchaValue: required(values.recaptchaValue, 'Please confirm you\'re not robot :)'),
    };

    return JSON.parse(JSON.stringify(errors));
  };

  return (
    <Formik
      initialValues={{
        email: '',
        nickname: '',
        paintingType: ['oil'],
        message: '',
        recaptchaValue: '',
      }}
      onSubmit={onSubmit}
      validate={validate}
    >
        {props => (
          <Form onSubmit={props.handleSubmit}>
            <Label htmlFor="paintingType">
              <Translate text={'Choose options'} />
            </Label>
            {props.errors.paintingType && props.touched.paintingType &&
            <Error>{props.errors.paintingType}</Error>}

            <Flex flexWrap={'wrap'} my={4} flexDirection={['column', 'column', 'row', 'row', 'row']}>
              {variants.length > 0 &&
                variants.map((variant) => (
                  <Box width={[1, 1, 1/3]} flex={['auto', 'auto', 1]}>
                    <Checkbox
                      id={variant.name}
                      value={variant.price.toString()}
                      name="paintingType"
                      label={variantNames[variant.name]}
                      price
                    />
                  </Box>
                ))
              }
            </Flex>

            <Label htmlFor="email">
              <Translate text={'E-mail'} />
            </Label>

            {props.errors.email && props.touched.email &&
            <Error>{props.errors.email}</Error>}
            <Input
              type="text"
              id="email"
              name="email"
              placeholder="Enter your email"
              onChange={props.handleChange}
              onBlur={props.handleBlur}
            />

            <Label htmlFor="nickname">
              <Translate text={'Your name'} />
            </Label>

            {props.errors.nickname && props.touched.nickname &&
            <Error>{props.errors.nickname}</Error>}

            <Input
              type="text"
              id="nickname"
              name="nickname"
              placeholder="Enter your name"
              onChange={props.handleChange}
              onBlur={props.handleBlur}
            />

            <Label htmlFor="message">
              <Translate text={'Anything else you would like to know'} />
            </Label>

            {props.errors.message && props.touched.message &&
            <Error>{props.errors.message}</Error>}
            <Textarea
              id="message"
              name="message"
              placeholder="Enter message"
              onChange={props.handleChange}
              onBlur={props.handleBlur}
            />

            {props.status && props.status.captchaError && <Error>{props.status.captchaError}</Error>}


            <Flex flexWrap={'wrap'} justifyContent={'center'}>
              <Box width={[1]}>
                {props.errors.recaptchaValue && props.touched.recaptchaValue &&
                <Error className="input-feedback">{props.errors.recaptchaValue}</Error>}
                <ReCAPTCHA
                  name="recaptchaValue"
                  sitekey={recaptchaKey}
                  ref={recaptchaRef}
                  onChange={(value) => props.setFieldValue("recaptchaValue", value)}
                />
              </Box>

              <Box style={{ textAlign: 'center'}} width={[1]} py={[3]}>
                <Button
                  type="submit"
                  disabled={props.isSubmitting}
                >
                  {props.isSubmitting && <span><LoaderStyle><Loader/></LoaderStyle>Loading</span>}
                  {!props.isSubmitting && <Translate text={'Send it to the author'} />}
                </Button>
              </Box>
            </Flex>

            {props.status && props.status.msg && <Success>{props.status.msg}</Success>}
          </Form>
        )}
    </Formik>
  );
};

export default BuyForm;
